import React, { useEffect, useState } from 'react';
import { message, Modal, Button, Form, Input } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useEntityData, useMutationUrn } from '../../EntityContext';
import { useAddLinkMutation, useUpdateLinkMutation } from '../../../../../graphql/mutations.generated';
import analytics, { EventType, EntityActionType } from '../../../../analytics';
import { useTranslation } from '../../../../../i18n/useTranslation';
import { useUserContext } from '../../../../context/useUserContext';
import {
    sanitizeUrl,
    sanitizeToPlainText,
    createUrlValidationRule,
    createTextValidationRule,
    createUrlBlurHandler,
    createTextBlurHandler,
    isContentSafe,
} from '../../../../shared/utils/sanitizationUtils';

export type LinkType = {
    link: string;
    label: string;
};

type AddLinkProps = {
    buttonProps?: Record<string, unknown>;
    refetch?: () => Promise<any>;
    isEdit?: boolean;
    existingLinks?: LinkType[];
    defaultValue?: LinkType;
};

export const AddLinkModal = ({ buttonProps, refetch, isEdit, existingLinks, defaultValue }: AddLinkProps) => {
    const { t } = useTranslation();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isFormValid, setIsFormValid] = useState(true);
    const mutationUrn = useMutationUrn();
    const user = useUserContext();
    const { entityType } = useEntityData();
    const [addLinkMutation] = useAddLinkMutation();
    const [updateLinkMutation] = useUpdateLinkMutation();

    const [form] = Form.useForm();

    useEffect(() => {
        if (defaultValue) {
            form.setFieldsValue({
                url: defaultValue.link,
                label: defaultValue.label,
            });
            setIsFormValid(true);
        }
    }, [defaultValue, form]);

    const showModal = () => {
        setIsModalVisible(true);
    };

    const handleClose = () => {
        form.resetFields();
        setIsModalVisible(false);
    };

    const handleAdd = async (formData: any) => {
        if (user?.urn) {
            // Sanitize form data before submission
            const sanitizedUrl = sanitizeUrl(formData.url);
            const sanitizedLabel = sanitizeToPlainText(formData.label);

            if (isEdit && defaultValue) {
                try {
                    await updateLinkMutation({
                        variables: {
                            input: {
                                currentUrl: defaultValue.link,
                                newUrl: sanitizedUrl,
                                label: sanitizedLabel,
                                resourceUrn: mutationUrn,
                            },
                        },
                    });
                    message.success({ content: t('Link Updated'), duration: 2 });
                    analytics.event({
                        type: EventType.EntityActionEvent,
                        entityType,
                        entityUrn: mutationUrn,
                        actionType: EntityActionType.UpdateLinks,
                    });
                } catch (e: unknown) {
                    message.destroy();
                    if (e instanceof Error) {
                        message.error({
                            content: t('Failed to update link: \n {{message}}', { message: e.message || '' }),
                            duration: 3,
                        });
                    }
                }
            } else {
                try {
                    await addLinkMutation({
                        variables: {
                            input: { linkUrl: sanitizedUrl, label: sanitizedLabel, resourceUrn: mutationUrn },
                        },
                    });
                    message.success({ content: t('Link Added'), duration: 2 });
                    analytics.event({
                        type: EventType.EntityActionEvent,
                        entityType,
                        entityUrn: mutationUrn,
                        actionType: EntityActionType.UpdateLinks,
                    });
                } catch (e: unknown) {
                    message.destroy();
                    if (e instanceof Error) {
                        message.error({
                            content: t('Failed to add link: \n {{message}}', { message: e.message || '' }),
                            duration: 3,
                        });
                    }
                }
            }
            refetch?.();
            handleClose();
        } else {
            message.error({ content: t('Error adding link: no user'), duration: 2 });
        }
    };

    return (
        <span>
            {isEdit ? (
                <Button onClick={showModal} type="text" shape="circle">
                    <EditOutlined />
                </Button>
            ) : (
                <Button
                    type="default"
                    data-testid="add-link-button"
                    icon={<PlusOutlined aria-hidden="true" />}
                    onClick={showModal}
                    {...buttonProps}
                >
                    {t('Add Link')}
                </Button>
            )}

            <Modal
                title={defaultValue ? t('Edit Link') : t('Add Link')}
                visible={isModalVisible}
                destroyOnClose
                className="f-modal"
                onCancel={handleClose}
                centered
                footer={[
                    <Button type="default" onClick={handleClose}>
                        {t('Cancel')}
                    </Button>,
                    <Button
                        type="primary"
                        data-testid="add-link-modal-add-button"
                        form="addLinkForm"
                        disabled={isFormValid}
                        key="submit"
                        htmlType="submit"
                    >
                        {defaultValue ? t('Update') : t('Add')}
                    </Button>,
                ]}
            >
                <Form
                    form={form}
                    name="addLinkForm"
                    onFinish={handleAdd}
                    onFieldsChange={(_, fields) => {
                        const isValueEmpty = fields.some((field) => !field.value);
                        const currentUrl = form.getFieldValue('url');
                        const currentLabel = form.getFieldValue('label');

                        // Check for content safety in real-time
                        const isUrlSafe = !currentUrl || isContentSafe(currentUrl);
                        const isLabelSafe = !currentLabel || isContentSafe(currentLabel);
                        const hasUnsafeContent = !isUrlSafe || !isLabelSafe;
                        const checkSameValue =
                            currentUrl === defaultValue?.link && currentLabel === defaultValue?.label;
                        setIsFormValid(
                            form.getFieldsError().some((field) => field.errors.length > 0) ||
                                isValueEmpty ||
                                checkSameValue ||
                                hasUnsafeContent,
                        );
                    }}
                    layout="vertical"
                >
                    <Form.Item
                        data-testid="add-link-modal-url"
                        name="url"
                        label={t('URL')}
                        rules={[
                            {
                                type: 'url',
                                required: true,
                                message: t('This field must be a valid url.'),
                            },
                            createUrlValidationRule(t, t('URL must use HTTP or HTTPS protocol')),
                            {
                                validator: (_, value) => {
                                    const existingLink = existingLinks?.find((link) => link.link === value);
                                    const isDuplicate = existingLink && (defaultValue?.link !== value || !isEdit);
                                    return isDuplicate
                                        ? Promise.reject(
                                              new Error(
                                                  t('This URL already exists with label {{label}}', {
                                                      label: existingLink?.label,
                                                  }),
                                              ),
                                          )
                                        : Promise.resolve();
                                },
                            },
                        ]}
                        initialValue={defaultValue?.link ?? undefined}
                    >
                        <Input
                            size="large"
                            placeholder="https://"
                            autoFocus
                            onBlur={createUrlBlurHandler(form, 'url')}
                        />
                    </Form.Item>
                    <Form.Item
                        name="label"
                        label={t('Label')}
                        className="mb-0"
                        data-testid="add-link-modal-label"
                        rules={[
                            {
                                required: true,
                                message: t('A label is required.'),
                            },
                            createTextValidationRule(t, 'Label', 100),
                        ]}
                        initialValue={defaultValue?.label ?? undefined}
                    >
                        <Input
                            size="large"
                            placeholder={t('A short label for this link')}
                            onBlur={createTextBlurHandler(form, 'label')}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </span>
    );
};
